<template>
  <div class="wrapper">
    <main>
      <article
        v-if="post"
        class="single-post">
        <div class="back-container">
          <a
            class="btn-back"
            @click="goBack()">
            <img
              class="arrow"
              src="@/assets/img/arrow-back.svg"
              alt="Back Arrow" />
            <p>Back</p>
          </a>
        </div>
        <div class="container">
          <div class="post-date-time">
            <p class="date">{{ formatDate(post.date) }}</p>
            <p class="tz">Eastern Standard Time</p>
          </div>
          <div class="post-title">
            <h1
              class="title h4"
              v-html="post.title.rendered"></h1>
          </div>
          <div
            v-if="post.featured_image"
            class="featured-image mt-10">
            <img
              :src="post.featured_image.large"
              :alt="post.featured_image.alt"
              class="w-full max-h-[550px] object-contain object-center" />
          </div>
          <div class="post-content font-larger">
            <template v-if="post.acf.post_external">
              <a :href="post.acf.post_external">Read More</a>
            </template>
            <template v-else>
              <div
                class="editor-formatting"
                v-html="post.content.rendered"></div>
            </template>
          </div>
        </div>
      </article>
    </main>
  </div>
</template>

<script setup>
import { inject, computed, onBeforeMount } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@/stores/main'
import useUtils from '@/components/composables/useUtils.js'

const router = useRouter()
const { decodeTitle } = useUtils()
const bgState = inject('bgState')

const store = useStore()
const props = defineProps({
  slug: {
    type: String,
    required: false,
  },
  type: {
    type: String,
    required: false,
  },
})

const request = {
  type: 'posts',
  slug: props.slug,
  showLoading: true,
}

const post = computed(() => {
  return store.singleBySlug(request)
})

const goBack = () => {
  if (window.history.length <= 2) router.push('/publications-press')
  else router.go(-1)
}

const getPost = async () => {
  store.getSingleBySlug(request)
  if (post.value) {
    store.updateDocTitle({
      parts: [decodeTitle(post.value.title.rendered), store.site.name],
    })
  }
}

const formatDate = (postDate) => {
  const date = new Date(postDate)

  const dateOptions = {
    month: 'long',
    year: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZone: 'America/New_York',
  }

  return new Intl.DateTimeFormat('en-US', dateOptions).format(date)
}

onBeforeMount(() => {
  bgState.value.isHomepage = false
  bgState.value.class = 'light'

  getPost()
})
</script>

<style lang="scss">
.single-post {
  padding: 220px 0 100px;

  @include tablet-landscape {
    padding: 150px 0 80px;
  }

  @include mobile {
    padding: 130px 0 60px;
  }

  .back-container {
    display: flex;
    padding: 0 39px 40px;

    @include mobile {
      padding: 0 20px 40px;
    }

    .btn-back {
      display: flex;
      justify-content: space-between;
      align-items: center;
      overflow: hidden;
      height: 50px;
      border-radius: 300px;
      padding-right: 20px;
      background-color: $yellow;
      color: $blue;

      .arrow {
        margin-left: -13px;
      }
    }
  }

  .post-date-time {
    margin: 0 0 50px;

    .date {
      color: $tech-blue;
    }

    .tz {
      color: $yellow;
    }
  }

  .post-content {
    margin: 40px 0 0;
  }

  .page-callout {
    padding-bottom: 0;
  }
}
</style>
